import axiosRetry, { isNetworkOrIdempotentRequestError } from 'axios-retry';
import { AxiosError, AxiosInstance, isAxiosError } from 'axios';

// Attached once per axios instance (see useAuthAxios.ts and the dedicated S3-upload
// instance in upload-utils.ts) rather than wrapped per call site: initiate_upload isn't
// idempotent on the backend, and retrying at the single-HTTP-request level means a
// retried-then-succeeded attempt only ever reaches the insert path once, never creating
// a second `files` row.

const DEFAULT_RETRIES = 3; // 4 total attempts for network/5xx/429
const AMBIGUOUS_STATUS_RETRIES = 1; // 2 total attempts for 403 -- see below

function isCancellationError(err: unknown): boolean {
    return (
        (isAxiosError(err) && err.code === 'ERR_CANCELED') ||
        (err instanceof Error && (err.name === 'AbortError' || err.name === 'CanceledError'))
    );
}

function retryCount(error: AxiosError): number {
    return error.config?.['axios-retry']?.retryCount ?? 0;
}

// 403 is ambiguous between "WAF rate-limit block" and "genuinely forbidden" -- until WAF
// returns 429 everywhere instead, give it a much smaller retry budget than the default so
// a truly-forbidden request doesn't hang.
export function retryCondition(error: AxiosError): boolean {
    if (isCancellationError(error)) return false;

    const status = error.response?.status;
    if (status === 403) return retryCount(error) < AMBIGUOUS_STATUS_RETRIES;
    if (status === 429) return true;

    // Everything else defers to axios-retry's own default: a network error retries
    // regardless of method (the request likely never reached the server), but a 5xx only
    // retries on an idempotent method. initiate_upload/create_ingested_file insert rows on
    // POST (the latter also enqueues an ETL job), so blindly retrying a 5xx there could
    // duplicate them; this also covers 400/401/404/413/422 etc, which are never retryable.
    return isNetworkOrIdempotentRequestError(error);
}

export function configureUploadRetry(instance: AxiosInstance): void {
    axiosRetry(instance, {
        retries: DEFAULT_RETRIES,
        retryCondition,
        retryDelay: axiosRetry.exponentialDelay,
    });
}

export interface UploadErrorInfo {
    status?: number;
    message: string;
    isCancellation: boolean;
}

// Same `response?.data?.detail` convention used across the app (e.g. features/auth/api.ts)
// for surfacing a FastAPI HTTPException's detail string, when there is one.
function backendDetail(err: AxiosError): string | undefined {
    const detail = (err.response?.data as { detail?: unknown } | undefined)?.detail;
    return typeof detail === 'string' ? detail : undefined;
}

export function describeUploadError(err: unknown): UploadErrorInfo {
    if (isCancellationError(err)) return { isCancellation: true, message: '' };
    if (isAxiosError(err)) {
        const status = err.response?.status;
        if (status === 413) return { status, message: 'File is too large to upload', isCancellation: false };
        if (status === 400 || status === 422)
            return { status, message: 'This file could not be uploaded', isCancellation: false };
        if (status === 401) return { status, message: 'Your session expired', isCancellation: false };
        if (status === 403 || status === 429)
            // A 403 isn't always WAF throttling -- the upload endpoints also return it for a
            // genuine permission denial, so prefer the backend's own detail when it sent one.
            return {
                status,
                message: backendDetail(err) ?? 'Upload service is busy — please try again',
                isCancellation: false,
            };
        if (status !== undefined && status >= 500)
            return { status, message: 'Server error while uploading', isCancellation: false };
        if (status === undefined) return { message: 'Network error while uploading', isCancellation: false };
        // Any other real response (e.g. generate_part_url's 404 when the upload row is
        // missing) is a genuine failure, not evidence of a corrupted file.
        return { status, message: backendDetail(err) ?? 'Upload failed', isCancellation: false };
    }
    return { message: 'Unable to upload corrupted folders or files', isCancellation: false };
}
